import { AsyncPipe, NgClass } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { EcmRouterModule, Link, LinkService, TrackingData } from '@ecm/router';

import { UniverseEnum } from '../../../../business/api/dashboard/universeEnum';
import { ActiveContractViewModel } from '../../../../business/model/crossroads/contracts-options/active-contract.view-model';
import { ContractsViewModel } from '../../../../business/model/crossroads/contracts.view-model';
import { TrackedPage } from '../../../../business/model/tracking/trackedPage.enum';
import { ParamsUtils } from '../../../../utils/params.utils';
import { AbstractComponent } from '../../../shared/abstract.component';
import { CommercialSubItemComponent } from '../../../shared/sub-item/commercial-sub-item/commercial-sub-item.component';
import { PictoSubItemComponent } from '../../../shared/sub-item/picto-sub-item/picto-sub-item.component';
import { CrossroadsUtils } from '../../utils/crossroads.utils';
import { CrossroadSuspendedAlertComponent } from '../crossroadSuspendedAlert/crossroadSuspendedAlert.component';

@Component({
    selector: 'active-contract',
    templateUrl: 'active-contract.html',
    styleUrls: ['active-contract.css'],
    standalone: true,
    imports: [
        NgClass,
        CrossroadSuspendedAlertComponent,
        PictoSubItemComponent,
        CommercialSubItemComponent,
        AsyncPipe,
        EcmRouterModule,
        TranslateModule,
    ],
})
export class ActiveContractComponent extends AbstractComponent implements OnInit {
    @Input()
    public viewModel: ContractsViewModel;

    @Input()
    public isVimEligible: boolean;

    @Input()
    public borderBottom = true;

    @Input()
    public universe: UniverseEnum;

    public mixedSoshPicto = 'assets/dashboard/images/mini-logo-sosh.svg';
    public mixedOrangePicto = 'assets/dashboard/images/mini-logo-orange.png';
    public icon: string;
    public activeContractViewModel: ActiveContractViewModel;

    public readonly ParamsUtils = ParamsUtils;

    /**
     * Generated link to contract page
     * @type {Link}
     */
    public openContractLink: Link;

    public labelButton: string;

    public inprogressLinkLabel: string;

    public alertTheme = 'alert-warning';
    public iconTheme = 'icon-Warning-important';
    /**
     * GA tracking area
     * @type {string}
     */
    private readonly trackingArea: string = 'care_selecteurcontrat_offre';

    private readonly translate = inject(TranslateService);
    private readonly linkService = inject(LinkService);

    public constructor() {
        super();
        this.translate.use('fr');
    }

    public ngOnInit(): void {
        super.ngOnInit();
        if (this.viewModel.contract) {
            this.activeContractViewModel = this.viewModel.contract as ActiveContractViewModel;
        }
        if (this.activeContractViewModel.koContract && !this.activeContractViewModel.koContract.isWarningAlert) {
            this.alertTheme = 'alert-info';
            this.iconTheme = 'icon-info';
        }
        if (this.universe === UniverseEnum.MIXED) {
            this.icon =
                CrossroadsUtils.getIconBrand(this.universe, this.viewModel, this.activeContractViewModel) ===
                UniverseEnum.SOSH
                    ? this.mixedSoshPicto
                    : this.mixedOrangePicto;
        }
        const isSosh = this.isSoshBrand();
        this.translate.get('dashboard.contractsAndOptions.button.offerAndOptions').subscribe((offerAndOptions: any) => {
            this.openContractLink =
                this.activeContractViewModel.type === 'fixe'
                    ? offerAndOptions['landLineLink']
                    : offerAndOptions['offerAndOptionLink'];
            // if isSosh = false hasFamilyAdvantages don't change
            if (isSosh || this.activeContractViewModel.brand.toUpperCase() === UniverseEnum.SOSH) {
                this.activeContractViewModel.hasFamilyAdvantages = false;
            }
            this.labelButton = this.getLabelButton(isSosh, offerAndOptions);
            if (this.activeContractViewModel.koContract?.hideButtonKoContract) {
                this.inprogressLinkLabel = this.getInprogressLinkLabel(isSosh);
            }
        });
    }

    public openWhoUseContract(): void {
        this.translate.get('dashboard.contractsAndOptions.whoUseContract').subscribe((whoUseContract: any) => {
            this.linkService.openLink(
                whoUseContract['link'],
                Object.assign(new TrackingData(), {
                    tmsTracking: {
                        trackedPage: TrackedPage.contractsAndOptions,
                        trackingArea: this.trackingArea,
                        trackingName: whoUseContract['label'],
                    },
                }),
                ParamsUtils.cidMap(this.activeContractViewModel.cid),
                whoUseContract['linkParam'],
            );
        });
    }

    public getLabelButton(isSosh: boolean, offerAndOptionsTranslate: any): string {
        if (!isSosh && this.activeContractViewModel.brand.toUpperCase() === UniverseEnum.SOSH) {
            return offerAndOptionsTranslate['soshLabel'];
        } else if (isSosh && this.viewModel.brand === 'Orange') {
            return offerAndOptionsTranslate['orangeLabel'];
        } else {
            return offerAndOptionsTranslate['label'];
        }
    }

    public getInprogressLinkLabel(isSosh: boolean): string {
        let linkLabel: string = this.activeContractViewModel.koContract.linkLabel;
        if (this.universe === UniverseEnum.MIXED) {
            if (isSosh && this.viewModel.brand.toUpperCase() === UniverseEnum.ORANGE) {
                linkLabel = this.activeContractViewModel.koContract.orangeLinkLabel;
            } else if (!isSosh && this.viewModel.brand.toUpperCase() === UniverseEnum.SOSH) {
                linkLabel = this.activeContractViewModel.koContract.soshLinkLabel;
            }
        }
        return linkLabel;
    }

    public openInprogressLink(): void {
        this.linkService.openLink(
            this.activeContractViewModel.koContract.link,
            Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.contractsAndOptions,
                    trackingArea: this.trackingArea,
                    trackingName: this.activeContractViewModel.koContract.linkLabel,
                },
            }),
        );
    }

    /**
     * Open button link
     */
    public openContract(): void {
        const isSosh = this.isSoshBrand();
        this.translate.get('dashboard.contractsAndOptions.button.offerAndOptions').subscribe((offerAndOptions: any) => {
            const link: Link =
                this.activeContractViewModel.type === 'fixe'
                    ? offerAndOptions['landLineLink']
                    : offerAndOptions['offerAndOptionLink'];
            let queryParam: NavigationExtras;
            let trackingNameParam: string = offerAndOptions['label'];
            if (!isSosh && this.activeContractViewModel.brand.toUpperCase() === UniverseEnum.SOSH) {
                queryParam = { queryParams: { sosh: '' } };
                trackingNameParam = offerAndOptions['soshLabel'];
            } else if (isSosh && this.activeContractViewModel.brand.toUpperCase() === UniverseEnum.ORANGE) {
                queryParam = { queryParams: { orange: '' } };
                trackingNameParam = offerAndOptions['orangeLabel'];
            } else {
                queryParam = null;
            }
            this.linkService.openLink(
                link,
                Object.assign(new TrackingData(), {
                    tmsTracking: {
                        trackedPage: TrackedPage.contractsAndOptions,
                        trackingArea: this.trackingArea,
                        trackingName: trackingNameParam,
                    },
                }),
                ParamsUtils.cidMap(this.activeContractViewModel.cid),
                queryParam,
            );
        });
    }
}
